// Step 4: İş Ekipmanı
import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { DocumentViewerDialog } from '../../employees/DocumentViewerDialog';
import { FirmDeficiencyActions, FirmDeficiencyExtra } from '../../documents/FirmDocCell';
import { FirmDocFileActions, FirmDocStatusBadge } from '../../documents/FirmDocFileActions';
import { EquipmentDocPanel } from '../../documents/EquipmentDocPanel';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { toast } from 'sonner';
import { useEquipmentKinds } from './EquipmentQuestion';
import { Truck, Plus, Loader2, Wrench, AlertTriangle, CalendarX, Eye, Boxes, X } from 'lucide-react';
import { checkDocumentValidity, getEquipmentDocs, getEquipmentStatus } from './wizardHelpers';
import { EmployeePoolDialog } from './EmployeePoolDialog';
import { BulkUploadDialog, missingDocs, bulkLabel, blocksEquipmentDoc } from './BulkUploadDialog';
import { TablePagination, paginate } from './TablePagination';
import { PageCountBadge } from '../../documents/PageCountBadge';
import { PageLimitTag } from '../../documents/PageLimitTag';

const WizardStep4 = ({
  equipment,
  setEquipment,
  selectedEquipment,
  setSelectedEquipment,
  formData,
  equipmentTypes,
  equipmentSubtypes,
  equipmentDialogOpen,
  setEquipmentDialogOpen,
  newEquipment,
  setNewEquipment,
  savingEquipment,
  handleEquipmentSelect,
  handleAddEquipment,
  getTemplatesForEquipment,
  operatorList,
  blockSignal = 0
}) => {
  const allKinds = useEquipmentKinds();
  const [viewerDoc, setViewerDoc] = useState(null);
  const [poolOpen, setPoolOpen] = useState(false);
  const [bulkEq, setBulkEq] = useState(null);
  const { isFirm, isReviewer, canCreate } = useAuth();
  // İnceleyen ekipman seçemez/kaldıramaz: yalnızca talepteki ekipmanlar listelenir
  // İSG: varsayılan sadece onay bekleyen (yüklenmiş, karar verilmemiş) ve eksik belgeler; buton ile tüm belgeler
  const [showAllDocs, setShowAllDocs] = useState(false);
  const reviewerHide = !isFirm && !showAllDocs;
  const reviewerAttention = (eq, key) => {
    const st = eq.documents?.[key] || 'beklemede';
    return ['eksik', 'suresi_doldu'].includes(st) || (st === 'beklemede' && !!eq.document_files?.[key]);
  };
  // Sadece talebe eklenen ekipmanlar listelenir; firma havuzdan seçer veya "Ekipman Ekle" ile ekler
  const visibleEquipment = (equipment || []).filter(eq => (formData?.equipment_ids || []).includes(eq.id)
    && (!reviewerHide || getEquipmentDocs(eq.equipment_type).some(d => reviewerAttention(eq, d.key))));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showBlocked, setShowBlocked] = useState(false);
  const blockedIds = new Set(showBlocked ? visibleEquipment.filter(eq => getEquipmentDocs(eq.equipment_type).some(d => blocksEquipmentDoc(eq, d))).map(eq => eq.id) : []);
  const pg = paginate(visibleEquipment, page, pageSize);
  const pageMarks = {};
  visibleEquipment.forEach((eq, i) => { if (blockedIds.has(eq.id)) { const p = Math.floor(i / pageSize) + 1; pageMarks[p] = (pageMarks[p] || 0) + 1; } });
  // İleri engellendiğinde ilk eksik ekipmanın sayfasını aç
  useEffect(() => {
    if (!blockSignal) return;
    setShowBlocked(true);
    const idx = visibleEquipment.findIndex(eq => getEquipmentDocs(eq.equipment_type).some(d => blocksEquipmentDoc(eq, d)));
    if (idx >= 0) setPage(Math.floor(idx / pageSize) + 1);
  }, [blockSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Ekipman belge durumunu güncelle - hem equipment hem selectedEquipment'ı güncelle
  const handleEquipmentDocChange = (eqId, docKey, value, currentEquipment) => {
    // Periyodik Muayene için Onay seçildiğinde tarih kontrolü
    if (docKey === 'periyodik_muayene' && value === 'onay') {
      const eq = currentEquipment || equipment.find(e => e.id === eqId);
      const existingDate = eq?.documents?.[`${docKey}_date`];
      if (!existingDate) {
        toast.error('Periyodik Muayene onayı için önce geçerlilik tarihi giriniz!');
        return;
      }
    }
    
    const updateDocs = (eq) => {
      if (eq.id !== eqId) return eq;
      return {
        ...eq,
        documents: {
          ...eq.documents,
          [docKey]: value
        }
      };
    };
    
    setEquipment(prev => prev.map(updateDocs));
    if (setSelectedEquipment) {
      setSelectedEquipment(prev => prev.map(updateDocs));
    }
  };
  
  const updateEquipmentState = (eqId, fn) => {
    const upd = (e) => (e.id === eqId ? fn(e) : e);
    setEquipment(prev => prev.map(upd));
    if (setSelectedEquipment) setSelectedEquipment(prev => prev.map(upd));
  };
  const updateEquipmentFile = (eqId, docKey, info) => updateEquipmentState(eqId, (e) => ({ ...e, documents: { ...(e.documents || {}), [docKey]: 'beklemede' }, document_files: { ...(e.document_files || {}), [docKey]: info } }));
  const removeEquipmentFile = (eqId, docKey, res) => updateEquipmentState(eqId, (e) => {
    const files = { ...(e.document_files || {}) };
    if (res.file) files[docKey] = res.file; else delete files[docKey];
    return { ...e, documents: { ...(e.documents || {}), [docKey]: res.status }, document_files: files };
  });
  const viewerEq = viewerDoc && equipment.find(e => e.id === viewerDoc.eqId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">İş Ekipmanı (Opsiyonel)</h3>
        {isFirm && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPoolOpen(true)} className="gap-1" data-testid="wizard-equipment-pool-btn">
              <Boxes className="h-4 w-4" />İş Ekipmanı Havuzu
            </Button>
            {canCreate('equipment') && <Button size="sm" variant="outline" onClick={() => setEquipmentDialogOpen(true)} className="gap-1" data-testid="wizard-add-equipment-btn">
              <Plus className="h-4 w-4" />Ekipman Ekle
            </Button>}
          </div>
        )}
      </div>
      {bulkEq && (
        <BulkUploadDialog
          entities={[bulkEq]} entityType="equipment" entityName={(e) => e.name || 'Ekipman'}
          getDocs={(e) => missingDocs(e, getEquipmentDocs(e.equipment_type))}
          onClose={() => setBulkEq(null)}
          onUploaded={(id, docKey, info) => updateEquipmentFile(id, docKey, info)}
        />
      )}
      {isFirm && (
        <EmployeePoolDialog
          kind="equipment" open={poolOpen} onOpenChange={setPoolOpen} formData={formData}
          employees={(equipment || []).map(eq => ({ ...eq, _kindLabel: allKinds.find(k => k.subtype_key === (eq.equipment_subtype || eq.subtype))?.subtype_label, _kindOff: !!formData.has_equipment && !(formData.equipment_kinds || []).includes(eq.equipment_subtype || eq.subtype) && !(formData.equipment_ids || []).includes(eq.id) }))}
          allowedKindLabels={formData.has_equipment ? (formData.equipment_kinds || []).map(k => allKinds.find(x => x.subtype_key === k)?.subtype_label || k) : null}
          selectedEmployees={(equipment || []).filter(eq => (formData?.equipment_ids || []).includes(eq.id))}
          onAdd={(list) => { list.forEach(eq => handleEquipmentSelect(eq.id, true)); toast.success(`${list.length} iş ekipmanı talebe eklendi`); }}
        />
      )}
      
      {(formData?.equipment_ids?.length || 0) > 0 && (
        <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-sm text-emerald-700 font-medium">{formData.equipment_ids.length} ekipman seçili</p>
        </div>
      )}
      
      {!isFirm && (
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="wizard-eq-reviewer-filter">
          <p className="text-xs text-amber-800">{showAllDocs ? 'Tüm belgeler gösteriliyor.' : 'Sadece onay bekleyen ve eksik belgeler gösteriliyor.'}</p>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => setShowAllDocs(v => !v)} data-testid="wizard-eq-reviewer-toggle-all-docs">
            {showAllDocs ? 'Sadece onay bekleyen ve eksikleri göster' : 'Tüm belgeleri göster'}
          </Button>
        </div>
      )}

      {(!visibleEquipment || visibleEquipment.length === 0) ? (
        <div className="text-center py-8 border rounded-lg bg-slate-50">
          <Truck className="h-12 w-12 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">{isFirm ? 'Henüz ekipman eklenmedi. İş Ekipmanı Havuzu\'ndan seçin veya yeni ekipman ekleyin.' : reviewerHide && (formData?.equipment_ids || []).length ? 'Onay bekleyen veya eksik ekipman belgesi yok' : 'Bu talepte ekipman bulunmuyor'}</p>
          {isFirm && (
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setPoolOpen(true)} className="gap-1" data-testid="wizard-eq-empty-pool-btn">
                <Boxes className="h-4 w-4" />İş Ekipmanı Havuzu
              </Button>
              {canCreate('equipment') && <Button size="sm" variant="outline" onClick={() => setEquipmentDialogOpen(true)} className="gap-1" data-testid="wizard-eq-empty-add-btn">
                <Plus className="h-4 w-4" />Yeni Ekipman Ekle
              </Button>}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {pg.rows.map((eq, rowIdx) => {
            const status = getEquipmentStatus(eq);
            const typeInfo = equipmentTypes?.[eq.equipment_type];
            const subKey = eq.equipment_subtype || eq.subtype;
            const subLabel = allKinds.find(k => k.subtype_key === subKey && k.main_type === eq.equipment_type)?.subtype_label || allKinds.find(k => k.subtype_key === subKey)?.subtype_label;
            const equipmentIds = formData?.equipment_ids || [];
            const isSelected = equipmentIds.includes(eq.id);
            
            // Ekipman belge geçerlilik kontrolü
            const equipmentValidityCheck = checkDocumentValidity(
              eq.documents, 
              formData.end_date,
              ['periyodik_muayene_date', 'operatorluk_belgesi_date', 'mali_mesuliyet_sigortasi_date']
            );
            const hasExpiredEquipmentDoc = !equipmentValidityCheck.valid && formData.end_date;
            const isBlocked = blockedIds.has(eq.id);
            
            return (
              <div key={eq.id} data-testid={`wizard-eq-row-${eq.id}`} data-blocked={isBlocked ? 'true' : undefined} data-review-pending={!isFirm && Object.keys(eq.document_files || {}).some(k => (eq.documents?.[k] || 'beklemede') === 'beklemede') ? 'true' : undefined} className={`border rounded-lg transition-colors ${isBlocked ? 'bg-red-50 border-red-500 ring-2 ring-red-500' : rowIdx % 2 ? 'bg-slate-100' : 'bg-white'} ${isBlocked ? '' : hasExpiredEquipmentDoc ? 'border-orange-300' : isSelected ? 'border-emerald-500' : ''}`}>
                {/* Ekipman başlık */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-3 border-b">
                  <span className="min-w-[1.25rem] text-right text-xs font-semibold text-slate-500 tabular-nums" data-testid={`wizard-eq-row-no-${eq.id}`}>{pg.offset + rowIdx + 1}.</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Checkbox 
                            checked={isSelected} 
                            onCheckedChange={(checked) => handleEquipmentSelect(eq.id, checked)}
                            disabled={!isFirm}
                            data-testid={`wizard-eq-select-${eq.id}`}
                          />
                        </div>
                      </TooltipTrigger>
                      {hasExpiredEquipmentDoc && (
                        <TooltipContent side="right" className="bg-orange-600 text-white max-w-xs">
                          <p className="font-medium mb-1">Talep aralığında süresi dolacak belgeler:</p>
                          <ul className="text-xs space-y-0.5">
                            {equipmentValidityCheck.expiredDocs.map((doc, i) => (
                              <li key={i}>• {doc.name}: {doc.expiryDate}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  <div className="flex-1 min-w-[12rem]">
                    <div className="flex flex-wrap items-center gap-2">
                      {eq.equipment_type === 'arac' ? <Truck className="h-4 w-4 text-slate-500" /> : <Wrench className="h-4 w-4 text-slate-500" />}
                      {subLabel && <span className="font-semibold text-slate-900" data-testid={`wizard-eq-kind-name-${eq.id}`}>{subLabel}</span>}
                      <span className={subLabel ? 'font-medium text-slate-600' : 'font-medium'}>{subLabel ? `– ${eq.name}` : eq.name}</span>
                      <Badge variant="outline" className="text-xs">{typeInfo?.label || eq.equipment_type}</Badge>
                      {isFirm && isSelected && missingDocs(eq, getEquipmentDocs(eq.equipment_type)).length > 0 && (
                        <Button type="button" size="sm" variant="outline" onClick={() => setBulkEq(eq)} className="ml-auto h-7 px-2 text-[11px] border-sky-300 text-sky-700 hover:bg-sky-50" data-testid={`wizard-eq-bulk-upload-${eq.id}`}>
                          Toplu yükle ({bulkLabel(missingDocs(eq, getEquipmentDocs(eq.equipment_type)))})
                        </Button>
                      )}
                      {hasExpiredEquipmentDoc && (
                        <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded flex items-center gap-1">
                          <CalendarX className="h-3 w-3" />
                          Talep Aralığında Süresi Dolmuş
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge className={status.color}>{status.label}</Badge>
                  {isFirm && (
                    <div className="ml-auto flex sm:min-w-[140px] justify-end pr-1">
                      {isSelected && (
                        <button type="button" onClick={async () => { const res = await handleEquipmentSelect(eq.id, false); if (res === true) toast.success(`${eq.name} talepten çıkarıldı`, { duration: 6000, action: { label: 'Geri Al', onClick: () => handleEquipmentSelect(eq.id, true) } }); }}
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-700" data-testid={`wizard-eq-remove-${eq.id}`}>
                          <X className="h-3.5 w-3.5" />Talepten Çıkar
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Talep aralığında süresi dolacak belgeler uyarısı */}
                {hasExpiredEquipmentDoc && (
                  <div className="mx-3 mt-3 p-2 bg-orange-100 border border-orange-200 rounded-lg">
                    <p className="text-xs text-orange-800 font-medium flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Talep bitiş tarihinden ({formData.end_date}) önce süresi dolacak belgeler:
                    </p>
                    <ul className="text-xs text-orange-700 mt-1 space-y-0.5">
                      {equipmentValidityCheck.expiredDocs.map((doc, i) => (
                        <li key={i}>• {doc.name} - Geçerlilik: {doc.expiryDate}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Belge durumları - sadece seçili ekipmanlar için göster */}
                {isSelected && (
                  <div className="p-3 space-y-3 bg-white">
                    <p className="text-xs text-slate-500 font-medium">Belge Durumları:</p>
                    {getEquipmentDocs(eq.equipment_type).filter(doc => !reviewerHide || reviewerAttention(eq, doc.key)).map(doc => {
                      const docStatus = eq.documents?.[doc.key] || 'beklemede';
                      
                      const hasFile = !!eq.document_files?.[doc.key];
                      const docDate = eq.documents?.[`${doc.key}_date`];
                      const note = eq.documents?.[`${doc.key}_note`];

                      return (
                        <div key={doc.key} className="space-y-1" data-testid={`wizard-eq-doc-row-${eq.id}-${doc.key}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm w-full sm:w-48">{doc.label}{isFirm && <PageLimitTag collection="equipment" docKey={doc.key} className="ml-1.5" />}</span>
                            <FirmDocStatusBadge status={docStatus} hasFile={hasFile} testId={`wizard-eq-doc-status-${eq.id}-${doc.key}`} hideEmpty={isFirm} />
                            {isReviewer && <PageCountBadge info={eq.document_files?.[doc.key]} testId={`wizard-eq-doc-pages-${eq.id}-${doc.key}`} />}
                            {isFirm && hasFile && docStatus === 'eksik' ? (
                              <FirmDeficiencyActions align="items-start"
                                entityType="equipment" entityId={eq.id} docKey={doc.key}
                                onUploaded={(info) => updateEquipmentFile(eq.id, doc.key, info)}
                                onView={() => setViewerDoc({ eqId: eq.id, docKey: doc.key, label: doc.label })}
                                testPrefix={`wizard-eq-doc-${eq.id}-${doc.key}`}
                              />
                            ) : isFirm ? (
                              <FirmDocFileActions
                                entityType="equipment" entityId={eq.id} docKey={doc.key} docLabel={doc.label} hasFile={hasFile} status={docStatus}
                                onUploaded={(info) => updateEquipmentFile(eq.id, doc.key, info)}
                                onDeleted={(res) => removeEquipmentFile(eq.id, doc.key, res)}
                                onView={() => setViewerDoc({ eqId: eq.id, docKey: doc.key, label: doc.label })}
                                testPrefix={`wizard-eq-doc-${eq.id}-${doc.key}`}
                                allowApprovedDelete
                                optional={!!doc.optional}
                              />
                            ) : hasFile ? (
                              <Button type="button" size="sm" variant="outline" className={`h-8 gap-1 ${docStatus === 'onay' ? 'border-rose-600 bg-rose-600 text-white hover:bg-rose-700 hover:text-white' : ''}`} onClick={() => setViewerDoc({ eqId: eq.id, docKey: doc.key, label: doc.label })} data-testid={`wizard-eq-doc-view-btn-${eq.id}-${doc.key}`}>
                                <Eye className="h-3.5 w-3.5" />{docStatus === 'onay' ? 'Görüntüle veya Eksik/Hatalı Bildir' : 'Görüntüle ve Onayla'}
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400">Firma henüz belge yüklemedi</span>
                            )}
                          </div>
                          {docStatus === 'onay' && docDate && <p className="ml-48 pl-2 text-xs text-slate-500">{doc.dateLabel || 'Tarih'}: {docDate}</p>}
                          {docStatus === 'onay' && eq.documents?.operatorluk_belgesi_name && doc.key === 'operatorluk_belgesi' && <p className="ml-48 pl-2 text-xs text-slate-500">{eq.documents.operatorluk_belgesi_name}</p>}
                          {docStatus === 'eksik' && note && <p className="ml-48 pl-2 text-xs text-rose-600">Red nedeni: {note}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <TablePagination total={visibleEquipment.length} page={pg.cur} totalPages={pg.totalPages} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} marks={pageMarks} prefix="wizard-eq" />
        </div>
      )}
      
      <DocumentViewerDialog
        open={!!viewerEq}
        onOpenChange={(open) => !open && setViewerDoc(null)}
        entity={viewerEq}
        entityType="equipment"
        subtitle={viewerEq?.name}
        docKey={viewerDoc?.docKey}
        docLabel={viewerDoc?.label}
        mode="latest"
        compact={isFirm}
        onReplaced={isReviewer && viewerEq ? (info) => updateEquipmentFile(viewerEq.id, viewerDoc.docKey, info) : undefined}
        extraInfo={viewerEq && isFirm && (
          <FirmDeficiencyExtra
            status={viewerEq.documents?.[viewerDoc.docKey]} docLabel={viewerDoc.label} note={viewerEq.documents?.[`${viewerDoc.docKey}_note`]}
            entityType="equipment" entityId={viewerEq.id} docKey={viewerDoc.docKey}
            onUploaded={(info) => { updateEquipmentFile(viewerEq.id, viewerDoc.docKey, info); setViewerDoc(null); }}
          />
        )}
        decisionPanel={viewerEq && isReviewer && (
          <EquipmentDocPanel
            key={`${viewerEq.id}-${viewerDoc.docKey}`}
            equipment={viewerEq}
            docKey={viewerDoc.docKey}
            docLabel={viewerDoc.label}
            templates={getTemplatesForEquipment(viewerEq.equipment_type, viewerEq.equipment_subtype || viewerEq.subtype, viewerDoc.docKey).map(t => t.template_text)}
            operatorList={operatorList || []}
            endDate={formData?.end_date}
            onSaved={(res) => { updateEquipmentState(res.id, (e) => ({ ...e, documents: res.documents, approved_document_files: res.approved_document_files })); setViewerDoc(null); }}
          />
        )}
      />

      {/* Ekipman Ekleme Dialog */}
      <Dialog open={equipmentDialogOpen} onOpenChange={setEquipmentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Yeni Ekipman Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Plaka Numarası / Seri No *</Label>
              <Input 
                value={newEquipment.name} 
                onChange={(e) => setNewEquipment(prev => ({ ...prev, name: e.target.value }))} 
                placeholder="örn: 34 ABC 123 veya SN-12345"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Ekipman Türü *</Label>
              <Select value={newEquipment.equipment_type} onValueChange={(val) => setNewEquipment(prev => ({ ...prev, equipment_type: val, subtype: '' }))}>
                <SelectTrigger><SelectValue placeholder="Tür seçin" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(equipmentTypes).map(([key, info]) => (
                    <SelectItem key={key} value={key}>{info.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {newEquipment.equipment_type && equipmentSubtypes.filter(st => st.main_type === newEquipment.equipment_type).length > 0 && (
              <div className="space-y-2">
                <Label>Alt Tür *</Label>
                <Select value={newEquipment.subtype} onValueChange={(val) => setNewEquipment(prev => ({ ...prev, subtype: val }))}>
                  <SelectTrigger><SelectValue placeholder="Alt tür seçin" /></SelectTrigger>
                  <SelectContent>
                    {equipmentSubtypes.filter(st => st.main_type === newEquipment.equipment_type).map((st) => (
                      <SelectItem key={st.id} value={st.subtype_key}>{st.subtype_label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {newEquipment.equipment_type && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-700 mb-2">
                  {newEquipment.equipment_type === 'arac' ? 'Araç için gerekli belgeler:' : 'İş Ekipmanı için gerekli belge:'}
                </p>
                <ul className="text-sm text-blue-600 space-y-1">
                  {getEquipmentDocs(newEquipment.equipment_type).map((doc, idx) => (
                    <li key={idx}>• {doc.label} {doc.hasDate && <span className="text-xs text-blue-400">({doc.dateLabel} girilecek)</span>}</li>
                  ))}
                </ul>
                <p className="text-xs text-blue-500 mt-2">* Belge durumları ekipman eklendikten sonra seçilebilir.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEquipmentDialogOpen(false)}>İptal</Button>
            <Button onClick={handleAddEquipment} disabled={savingEquipment}>
              {savingEquipment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WizardStep4;
